/* eslint-disable camelcase */
import React, { createContext, useState } from 'react';
import PropTypes from 'prop-types';

export const BundleLookPageContext = createContext({});

const BundleLookPageProvider = ({ bundle, byoBundle, onAddToCart, onRemoveFromCart, children, ...rest }) => {
  /**
   * In looks, we need to listen to the global cart to add/remove items from the progress bar
   * So the state could be updated by add/delete button in the look pdp or from the cart
   * This derived state will be used to check against the current state later to determine
   * where is the source of the truth of the state
   */
  const derivedState = byoBundle.name.reduce((acc, name, index) => {
    const { groups, comparedPrice, addedCount } = acc;
    const { group_count, icon, tag, groupsSelectedVariants } = byoBundle;
    const groupCount = group_count ? Number(group_count[index]) : null;
    const selectedVariants = groupsSelectedVariants ? groupsSelectedVariants[index] || [] : [];

    return {
      groups: [
        ...groups,
        {
          name,
          selectedCount: selectedVariants.length,
          groupCount,
          icon: icon[index],
          tag: tag[index],
          products: groupCount ? [...[...Array(groupCount)].map(() => ({ variantId: null }))] : selectedVariants,
        }
      ],
      comparedPrice: selectedVariants.reduce((sum, variant) => sum + variant.price, comparedPrice),
      addedCount: addedCount + selectedVariants.length
    };
  }, {
    groups: [],
    comparedPrice: 0,
    addedCount: 0,
  });

  const [state, setState] = useState(null);
  const [prevAddedCount, setPrevAddedCount] = useState(null);

  /**
   * This is the HOOK's version of getDerivedStateFromProps
   * Ref: https://reactjs.org/docs/hooks-faq.html#how-do-i-implement-getderivedstatefromprops
   */
  if (prevAddedCount !== derivedState.addedCount) {
    setState(derivedState);
    setPrevAddedCount(derivedState.addedCount);
  }

  const onAddProduct = (groupPosition, product) => {
    if (onAddToCart) {
      onAddToCart(product.variantId, 1, false, false);
    } else {
      const { addedCount, comparedPrice, groups } = state;
      const { selectedCount, groupCount, products } = groups[groupPosition];
      if (groupCount && selectedCount >= groupCount) return; // early exit if group is filled in full
      const productPosition = products.findIndex((item) => !item.variantId);
      setState({
        addedCount: addedCount + 1,
        comparedPrice: comparedPrice + product.price,
        groups: [
          ...groups.slice(0, groupPosition),
          {
            ...groups[groupPosition],
            selectedCount: selectedCount + 1,
            products: groupCount ? Object.assign([], products, { [productPosition]: product }) : [...products, product]
          },
          ...groups.slice(groupPosition + 1)
        ]
      });
    }
  };

  const onDeleteProduct = (groupPosition, productPosition) => {
    const { addedCount, comparedPrice, groups } = state;
    const { selectedCount, products, groupCount } = groups[groupPosition];
    const { price, variantId } = products[productPosition];

    if (onRemoveFromCart) {
      onRemoveFromCart(variantId);
    } else {
      setState({
        addedCount: addedCount - 1,
        comparedPrice: comparedPrice - price,
        groups: [
          ...groups.slice(0, groupPosition),
          {
            ...groups[groupPosition],
            selectedCount: selectedCount - 1,
            products: groupCount ? Object.assign([], products, { [productPosition]: { variantId: null } }) : products.filter((item, i) => i !== productPosition)
          },
          ...groups.slice(groupPosition + 1)
        ]
      });
    }
  };

  return (
    <BundleLookPageContext.Provider value={{
      bundle: bundle ? { ...bundle, totalCount: Number(bundle.totalCount) } : null,
      onAddProduct,
      onDeleteProduct,
      ...rest,
      ...state
    }}
    > {children}
    </BundleLookPageContext.Provider>
  );
};

BundleLookPageProvider.propTypes = {
  children: PropTypes.node.isRequired,
  byoBundle: PropTypes.object.isRequired,
  bundle: PropTypes.object,
  onAddToCart: PropTypes.func,
  onRemoveFromCart: PropTypes.func
};

BundleLookPageProvider.defaultProps = {
  bundle: null,
  onAddToCart: null,
  onRemoveFromCart: null
};

export { BundleLookPageProvider };
